import { Alert } from "PFComponents/alert";
import { profileHash } from "PFCore/helpers/profile_hash";
import roundtoWholeNumber from "PFCore/helpers/round_to_whole_number";
import PropTypes from "prop-types";
import React from "react";

import getPeriod from "../helpers/get_period";
import css from "./docx_preview.less";
import PagePreview from "./page_preview";

const DocxPreview = ({ profile, order, config, avatar }) => {
  const { color } = config;

  return (
    <>
      {profile.enabled === false && (
        <Alert kind="warning" title="Disabled" style={{ marginBottom: 20, marginTop: 20 }}>
          This profile won&apos;vt be exported when exporting all profiles at once because it&apos;vs disabled
          in the <b>Profiles</b> tab
        </Alert>
      )}
      <PagePreview>
        {/* checking the typeof as 0 needs to be evaluated as number and not as false */}
        {typeof profile.score === "number" && (
          <div style={{ marginBottom: 10 }}>Score: {roundtoWholeNumber(profile.score * 100)}%</div>
        )}
        {avatar && !config.isBlindModeOn && config.showAvatar && (
          <img className={css.avatar} src={avatar} alt="" />
        )}
        <strong className={css.profileName} style={{ color }}>
          {config.isBlindModeOn ? `Candidate ${profileHash(profile.id)}` : profile.name}
        </strong>
        <div className={css.jobTitle}>{profile.jobTitle}</div>
        <div className={css.companyName}>{profile.companyName}</div>

        {!config.isBlindModeOn && (
          <Contacts contactInformationItems={profile.contactInformationItems} color={color} />
        )}

        {order.map((kind) => {
          if (config.hidden[kind]) {
            return false;
          } else if (kind === "experience") {
            return <Positions key={kind} positions={profile.positions} color={color} />;
          } else if (kind === "summary") {
            return <Summary key={kind} summary={profile.summary} color={color} />;
          } else {
            return <CustomField key={kind} customTypes={profile.customTypes} kind={kind} color={color} />;
          }
        })}
      </PagePreview>
    </>
  );
};

const Summary = ({ summary, color }) => {
  if (!summary) {
    return false;
  }

  return (
    <div className={css.summaryWrap}>
      <strong className={css.subheader} style={{ color }}>
        Summary
      </strong>
      <p className={css.summary}>{summary}</p>
    </div>
  );
};

Summary.propTypes = {
  summary: PropTypes.string,
  color: PropTypes.string
};

const CustomField = ({ customTypes, kind, color }) => {
  const customField = customTypes.find(({ type }) => type.name === kind);

  if (!customField || !customField.text) {
    return false;
  }

  return (
    <div>
      <strong className={css.subheader} style={{ color }}>
        {customField.displayAs}
      </strong>
      <p>{customField.text}</p>
    </div>
  );
};

const customFieldShape = PropTypes.shape({
  name: PropTypes.string
});

CustomField.propTypes = {
  customTypes: PropTypes.arrayOf(customFieldShape),
  kind: PropTypes.string,
  color: PropTypes.string
};

const Positions = ({ positions, color }) => {
  if (!positions?.items.length) {
    return false;
  }

  return (
    <>
      <strong className={css.subheader} style={{ color }}>
        {positions.header}
      </strong>
      {positions.items.map(({ id, jobTitle, startDate, endDate, current, summary }) => (
        <div className={css.position} key={id}>
          <strong>{jobTitle}</strong>
          {startDate && <small>{getPeriod(startDate, endDate, current)}</small>}
          <p>{summary}</p>
        </div>
      ))}
    </>
  );
};

const positionItemShape = PropTypes.shape({
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      jobTitle: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      current: PropTypes.bool,
      summary: PropTypes.string
    })
  ),
  header: PropTypes.string
});

Positions.propTypes = {
  positions: positionItemShape,
  color: PropTypes.string
};

const Contacts = ({ contactInformationItems, color }) => {
  if (contactInformationItems.length === 0) {
    return false;
  }

  return (
    <>
      <strong className={css.subheader} style={{ color }}>
        Contact
      </strong>
      {contactInformationItems.map((cf, index) => (
        <div key={`contract_information_${index}`}>
          <b>{cf.type}:</b>
          <span>{` ${cf.text}`}</span>
        </div>
      ))}
    </>
  );
};

const contactInformationItemShape = PropTypes.shape({
  type: PropTypes.string,
  text: PropTypes.string
});

Contacts.propTypes = {
  contactInformationItems: PropTypes.arrayOf(contactInformationItemShape),
  color: PropTypes.string
};

DocxPreview.propTypes = {
  profile: PropTypes.shape({
    id: PropTypes.number,
    enabled: PropTypes.bool,
    score: PropTypes.number,
    name: PropTypes.string,
    jobTitle: PropTypes.string,
    companyName: PropTypes.string,
    summary: PropTypes.string,
    contactInformationItems: PropTypes.arrayOf(contactInformationItemShape),
    positions: positionItemShape,
    customTypes: PropTypes.arrayOf(customFieldShape)
  }),
  config: PropTypes.shape({
    color: PropTypes.string,
    hidden: PropTypes.object,
    isBlindModeOn: PropTypes.bool,
    showAvatar: PropTypes.bool
  }),
  order: PropTypes.arrayOf(PropTypes.string),
  avatar: PropTypes.string
};

export default DocxPreview;
